/**
 * Is the viewport below the `sm` breakpoint (640px)?
 *
 * NOT `useIsMobile`, and not `useIsCompactViewport`. Those answer different
 * questions at different boundaries — 767px, the locked M1/M2 layout-shell
 * breakpoint, and 1023px, where the Bexio sync detail swaps a column for an
 * overlay. This boundary is the occupancy matrix's own geometry, measured
 * rather than chosen: the table is 200px of sticky room column plus 60px per
 * month, so showing the smallest period the control offers — six months —
 * without sideways scrolling needs 560px of content, which a viewport reaches
 * at 592px once the page's 16px padding is paid. `sm` is the nearest existing
 * breakpoint above that. Below it the matrix has fewer than six months of room
 * and `CoLivingOccupancyMobile` takes over.
 *
 * A HOOK RATHER THAN `sm:hidden`, deliberately. CSS would leave BOTH
 * presentations in the DOM: two sets of transition popovers to keep in step,
 * twice the nodes for a portfolio of any size, and — because jsdom applies no
 * CSS — every existing query in the page's regression net matching twice. The
 * hook renders exactly one.
 *
 * The query is written as `max-width` for the same reason the other two are:
 * jsdom ships no `matchMedia`, so a test viewport reads as NOT narrow and
 * exercises the desktop matrix, which is the path this page was built around.
 * A test that wants the phone view mocks `window.matchMedia` explicitly.
 */
import { useEffect, useState } from "react";

const NARROW_QUERY = "(max-width: 639px)";

function readMatch() {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
    return false;
  }
  return window.matchMedia(NARROW_QUERY).matches;
}

export function useIsNarrowViewport() {
  const [narrow, setNarrow] = useState(readMatch);

  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
      return undefined;
    }
    const mq = window.matchMedia(NARROW_QUERY);
    const onChange = (e) => setNarrow(e.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  return narrow;
}
